import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import { LiveChatIntegration } from '@/shared/types/integration';
import { welcomeTriggerMessageValidator } from '@/util/validation/rules/integration-rules';

import { CardBaseHeader } from '../Components/CardBase';

interface Props {
  integration: LiveChatIntegration;
  canWrite: boolean;
}

type WelcomeTriggerForm = { welcome_trigger_message: string };

const FORM_ID = 'livechat-welcome-trigger-form';

const WelcomeTriggerCard = ({ integration, canWrite }: Props) => {
  const { t } = useTranslation();
  const { updateIntegration, updateStatus } = useIntegrations<LiveChatIntegration>(
    integration.desk_id,
    integration.integration_id
  );

  const form = useForm<WelcomeTriggerForm>({
    defaultValues: { welcome_trigger_message: integration.config?.welcome_trigger_message ?? '' },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  const onSubmit = (values: WelcomeTriggerForm) => {
    const welcomeTriggerMessage = values.welcome_trigger_message.trim();

    updateIntegration(
      {
        ...integration,
        config: {
          ...integration.config,
          welcome_trigger_message: welcomeTriggerMessage || undefined,
        },
      },
      { onSuccess: () => reset({ welcome_trigger_message: welcomeTriggerMessage }) }
    );
  };

  return (
    <div className="w-full">
      <CardBaseHeader
        title={t('integrations.livechat.welcome_trigger.title')}
        titleVariant="secondary"
      />
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <FormField
              control={control}
              name="welcome_trigger_message"
              rules={{
                maxLength: {
                  value: welcomeTriggerMessageValidator.maxLength,
                  message: t('validation.max_length', {
                    0: welcomeTriggerMessageValidator.maxLength,
                  }),
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('integrations.livechat.welcome_trigger.label')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('integrations.livechat.welcome_trigger.subtitle')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};

export default WelcomeTriggerCard;
